import type { IconName } from './types'

/** Описание одной иконки: контурные пути + точки-заливки (глаза, центр). */
export type IconGlyph = {
  viewBox: string
  paths?: string[]
  circles?: Array<{ cx: number; cy: number; r: number }>
  rects?: Array<{ x: number; y: number; width: number; height: number; rx?: number }>
  /** Мелкие заполненные точки (currentColor, без обводки). */
  dots?: Array<{ cx: number; cy: number; r: number }>
}

/**
 * Один визуальный язык: контур, без заливки, currentColor.
 * Пути взяты из UI-кита (`docs/product/mockups/Главная - дизайн-система.dc.html`),
 * где кита нет — дорисованы в том же стиле.
 */
export const ICONS: Record<IconName, IconGlyph> = {
  home: {
    viewBox: '0 0 20 20',
    paths: ['M3 9.5 10 4l7 5.5V16a1 1 0 0 1-1 1h-3v-5H8v5H4a1 1 0 0 1-1-1z'],
  },
  heart: {
    viewBox: '0 0 20 20',
    paths: [
      'M10 16.5S3.5 12.6 3.5 8.4A3.6 3.6 0 0 1 10 6.2a3.6 3.6 0 0 1 6.5 2.2c0 4.2-6.5 8.1-6.5 8.1z',
    ],
  },
  links: {
    viewBox: '0 0 20 20',
    paths: ['M5.5 6.5 14.5 5.5M5.5 6.5 10 14.5M14.5 5.5 10 14.5'],
    circles: [
      { cx: 5.5, cy: 6.5, r: 1.5 },
      { cx: 14.5, cy: 5.5, r: 1.5 },
      { cx: 10, cy: 14.5, r: 1.8 },
    ],
  },
  stats: {
    viewBox: '0 0 20 20',
    paths: ['M4 16v-4M8.7 16V6.5M13.3 16v-6.5M18 16V4.5'],
  },
  profile: {
    viewBox: '0 0 20 20',
    paths: ['M4.5 16.5c0-3 2.5-4.6 5.5-4.6s5.5 1.6 5.5 4.6'],
    circles: [{ cx: 10, cy: 7, r: 3 }],
  },
  'chevron-right': { viewBox: '0 0 20 20', paths: ['M8 4l6 6-6 6'] },
  'chevron-left': { viewBox: '0 0 20 20', paths: ['M12 4 6 10l6 6'] },
  'chevron-down': { viewBox: '0 0 20 20', paths: ['M4 8l6 6 6-6'] },
  'arrow-left': { viewBox: '0 0 20 20', paths: ['M12 4 6 10l6 6'] },
  bell: {
    viewBox: '0 0 20 20',
    paths: [
      'M10 3.5a4 4 0 0 0-4 4v2.3c0 .9-.3 1.8-.9 2.5L4 13.8h12l-1.1-1.5a4 4 0 0 1-.9-2.5V7.5a4 4 0 0 0-4-4z',
      'M8.2 16a1.8 1.8 0 0 0 3.6 0',
    ],
  },
  plus: { viewBox: '0 0 20 20', paths: ['M10 5v10M5 10h10'] },
  share: {
    viewBox: '0 0 20 20',
    paths: ['M10 13V3M10 3 6.5 6.5M10 3l3.5 3.5M4 12v3.5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V12'],
  },
  calendar: {
    viewBox: '0 0 20 20',
    paths: ['M7 4v3M13 4v3'],
    rects: [{ x: 3.5, y: 5.5, width: 13, height: 11, rx: 2.5 }],
  },
  clock: {
    viewBox: '0 0 20 20',
    paths: ['M10 6.5V10l3 2'],
    circles: [{ cx: 10, cy: 10, r: 7 }],
  },
  pin: {
    viewBox: '0 0 20 20',
    paths: ['M10 17s5.5-4.8 5.5-8.8A5.5 5.5 0 0 0 4.5 8.2C4.5 12.2 10 17 10 17z'],
    circles: [{ cx: 10, cy: 8.2, r: 1.8 }],
  },
  search: {
    viewBox: '0 0 20 20',
    paths: ['M13 13l3 3'],
    circles: [{ cx: 9, cy: 9, r: 5 }],
  },
  check: { viewBox: '0 0 20 20', paths: ['M4.5 10.5 8.5 14.5 15.5 6.5'] },
  close: { viewBox: '0 0 20 20', paths: ['M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5'] },
  more: {
    viewBox: '0 0 20 20',
    dots: [
      { cx: 5, cy: 10, r: 1.1 },
      { cx: 10, cy: 10, r: 1.1 },
      { cx: 15, cy: 10, r: 1.1 },
    ],
  },
  pencil: {
    viewBox: '0 0 20 20',
    paths: ['M5 15l.7-3.2L12.5 5 15 7.5 8.2 14.3 5 15z', 'M11 6.5 13.5 9'],
  },
  refresh: {
    viewBox: '0 0 20 20',
    paths: [
      'M15.5 5.5v3.5H12M4.5 14.5V11H8',
      'M15.5 9A5.5 5.5 0 0 0 6 6.3M4.5 11A5.5 5.5 0 0 0 14 13.7',
    ],
  },
  // Пляжный зонт, а не пальма (правка основателя: «иконка отпуска слишком непохожая на отпуск»).
  // Прежняя пальма в 18px — размере недели состояний — читалась цветком в горшке: три дуги
  // кроны сливались в одно пятно. Зонт держит форму на мелком размере, а наклон купола и
  // косая ножка отличают его от гриба и настольной лампы.
  parasol: {
    viewBox: '0 0 20 20',
    paths: ['M11.4 10.2 9.6 17', 'M4 12.2a6.2 6.2 0 0 1 11.9-3.3z', 'M6.5 17.5h7'],
  },
  bowl: {
    viewBox: '0 0 20 20',
    paths: ['M4 11h12a6 6 0 0 1-12 0z', 'M6.5 11V9.5a3.5 3.5 0 0 1 7 0V11'],
  },
  star: {
    viewBox: '0 0 20 20',
    paths: ['M10 3.5 11.8 8 16.5 8.4 13 11.5l1.1 4.6L10 13.7 5.9 16.1 7 11.5 3.5 8.4 8.2 8z'],
  },
  sparkle: {
    viewBox: '0 0 20 20',
    paths: [
      'M10 2.5c.5 3.4 1.9 6.1 4.5 7.5-2.6 1.4-4 4.1-4.5 7.5-.5-3.4-1.9-6.1-4.5-7.5 2.6-1.4 4-4.1 4.5-7.5z',
    ],
  },
  'eye-off': {
    viewBox: '0 0 20 20',
    paths: [
      'M2.5 2.5l15 15',
      'M7.8 7.9a2 2 0 0 0 2.8 2.8',
      'M4.3 5.7C2.8 6.9 1.6 8.4 1 10c1.4 3.2 4.9 5.7 9 5.7 1.4 0 2.7-.3 3.9-.8',
      'M11.9 4.6c.7-.1 1.4-.2 2.1-.2 4.1 0 7.6 2.5 9 5.6-.5 1.1-1.2 2.1-2.1 3',
    ],
  },
  trash: {
    viewBox: '0 0 20 20',
    paths: [
      'M4.5 6h11',
      'M8 6V4.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V6',
      'M6 6l.7 9a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L14 6',
      'M9 9v5M11 9v5',
    ],
  },
  lock: {
    viewBox: '0 0 20 20',
    paths: ['M7.5 9V7a2.5 2.5 0 0 1 5 0v2'],
    rects: [{ x: 5, y: 9, width: 10, height: 7, rx: 2 }],
  },
  info: {
    viewBox: '0 0 20 20',
    paths: ['M10 9.3v4.2'],
    circles: [{ cx: 10, cy: 10, r: 7 }],
    dots: [{ cx: 10, cy: 6.7, r: 0.9 }],
  },
  // Добавлена для уведомлений (Notification) — среди готовых иконок нет подходящей
  // для WARNING, а спека 01 не описывает её отдельно.
  warning: {
    viewBox: '0 0 20 20',
    paths: ['M10 3.5 17.3 15.8H2.7Z', 'M10 8.6v3.3'],
    dots: [{ cx: 10, cy: 14.3, r: 0.9 }],
  },
  // Восклицательный знак в круге — бейдж уведомления об ошибке. Крестик там занят кнопкой
  // закрытия справа, два крестика в одной строке читались как опечатка.
  alert: {
    viewBox: '0 0 20 20',
    paths: ['M10 6.2v4.4'],
    circles: [{ cx: 10, cy: 10, r: 7 }],
    dots: [{ cx: 10, cy: 13.6, r: 0.9 }],
  },
  sun: {
    viewBox: '0 0 20 20',
    paths: [
      'M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2',
      'M4.6 4.6l1.4 1.4M14 14l1.4 1.4M15.4 4.6 14 6M6 14l-1.4 1.4',
    ],
    circles: [{ cx: 10, cy: 10, r: 4 }],
  },
  moon: {
    viewBox: '0 0 20 20',
    paths: ['M13.5 3.5a7 7 0 1 0 0 13 8.5 8.5 0 0 1 0-13z'],
  },
  ascendant: {
    viewBox: '0 0 20 20',
    paths: ['M7 11.5 10 7l3 4.5', 'M10 7v6.5'],
    circles: [{ cx: 10, cy: 10, r: 7 }],
  },
}
